import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import PhoneIcon from '@mui/icons-material/Phone';
import GavelIcon from '@mui/icons-material/Gavel';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CourtEventApi, {
  APPEARANCE_LABELS,
  AppearanceNoticePreview,
  AppearanceNotRequiredNotice,
  AppearanceRequirement,
} from '../../api/CourtEventApi';
import CourtSelect from '../CourtSelect/CourtSelect';

type CourtChoice = React.ComponentProps<typeof CourtSelect>['value'];

/**
 * Does the client have to attend? (Hannah, Aug 8 2026 → Lloyd, Aug 10 → 29 Oct 2026.)
 *
 *   · The provenance is shown: "confirmed by Kelsi" or "unconfirmed".
 *   · Choosing "must appear" emails the client, so it asks first and previews the message.
 *   · Choosing "does not need to appear" saves immediately. If the setting is in the future
 *     and the switch is ON, an editable preview dialog offers to tell the client with Send
 *     and Don't send.
 *   · On a future setting already saved as "does not need to appear", a "Tell the client"
 *     (or "Client told on <date> by <staff>" + "Tell again") button opens the preview.
 */

interface AppearanceControlProps {
  courtEventGuid: string;
  value: AppearanceRequirement;
  setBy?: string | null;
  setAt?: string | null;
  toldNotRequiredAt?: string | null;
  toldNotRequiredBy?: string | null;
  /** False for reminders and deadlines — the question does not apply and the control hides. */
  isHearing?: boolean;
  /** Epoch ms. A setting in the past never notifies, and the dialog says so. */
  startEpoch: number;
  onChanged: (next: AppearanceRequirement) => void;
  disabled?: boolean;
}

const ICONS: Record<AppearanceRequirement, React.ReactElement> = {
  NOT_REQUIRED: <CheckCircleOutlineIcon fontSize="small" />,
  IN_PERSON: <GavelIcon fontSize="small" />,
  BY_VIDEO: <VideocamIcon fontSize="small" />,
  BY_PHONE: <PhoneIcon fontSize="small" />,
};

const AppearanceControl: React.FC<AppearanceControlProps> = ({
  courtEventGuid,
  value,
  setBy,
  setAt,
  toldNotRequiredAt,
  toldNotRequiredBy,
  isHearing = true,
  startEpoch,
  onChanged,
  disabled = false,
}) => {
  // Local state for the displayed dropdown value prevents desync after save (display bug #8)
  const [currentValue, setCurrentValue] = useState<AppearanceRequirement>(value);
  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  const [toldAt, setToldAt] = useState<string | null>(toldNotRequiredAt ?? null);
  const [toldBy, setToldBy] = useState<string | null>(toldNotRequiredBy ?? null);
  useEffect(() => {
    setToldAt(toldNotRequiredAt ?? null);
    setToldBy(toldNotRequiredBy ?? null);
  }, [toldNotRequiredAt, toldNotRequiredBy]);

  const [switchEnabled, setSwitchEnabled] = useState<boolean>(true);
  useEffect(() => {
    CourtEventApi.getAppearanceSettings()
      .then((res) => setSwitchEnabled(res.notRequiredNotifyEnabled))
      .catch(() => setSwitchEnabled(true));
  }, []);

  const [pending, setPending] = useState<AppearanceRequirement | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // For must-attend notice preview
  const [courtChoice, setCourtChoice] = useState<CourtChoice | null | undefined>(undefined);
  const [preview, setPreview] = useState<AppearanceNoticePreview | null>(null);
  const [previewSkipped, setPreviewSkipped] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // For excused / not-required dialog
  const [notRequiredDialogOpen, setNotRequiredDialogOpen] = useState(false);
  const [notRequiredPreview, setNotRequiredPreview] = useState<AppearanceNotRequiredNotice | null>(null);
  const [notRequiredLoading, setNotRequiredLoading] = useState(false);
  const [notRequiredSending, setNotRequiredSending] = useState(false);
  const [notRequiredError, setNotRequiredError] = useState<string | null>(null);
  const [editedSubject, setEditedSubject] = useState<string>('');
  const [editedBody, setEditedBody] = useState<string>('');

  const isPast = startEpoch < Date.now();
  const previewWanted = pending !== null && pending !== 'NOT_REQUIRED' && !isPast;
  const chosenGuid = courtChoice === undefined ? undefined : courtChoice?.Court_guid ?? null;

  useEffect(() => {
    if (!previewWanted || pending === null) return;
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    CourtEventApi.previewAppearanceNotice({
      CourtEvent_guid: courtEventGuid,
      CourtEvent_AppearanceRequirement: pending,
      ...(chosenGuid !== undefined ? { CourtEvent_Court_guid: chosenGuid } : {}),
    })
      .then((result) => {
        if (cancelled) return;
        setPreview(result.notice);
        setPreviewSkipped(result.skippedReason);
      })
      .catch((err) => {
        if (!cancelled) setPreviewError(err instanceof Error ? err.message : 'Could not build the preview');
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [previewWanted, pending, chosenGuid, courtEventGuid]);

  const closePendingDialog = () => {
    setPending(null);
    setCourtChoice(undefined);
    setPreview(null);
    setPreviewSkipped(null);
    setPreviewError(null);
  };

  const openNotRequiredDialog = async () => {
    setNotRequiredDialogOpen(true);
    setNotRequiredLoading(true);
    setNotRequiredError(null);
    try {
      const res = await CourtEventApi.previewAppearanceNotRequiredNotice({
        CourtEvent_guid: courtEventGuid,
      });
      if (res.notice) {
        setNotRequiredPreview(res.notice);
        setEditedSubject(res.notice.subject);
        setEditedBody(res.notice.body);
      } else if (res.skippedReason) {
        setNotRequiredError(`Cannot notify: ${res.skippedReason}`);
      }
    } catch (err) {
      setNotRequiredError(err instanceof Error ? err.message : 'Failed to load preview');
    } finally {
      setNotRequiredLoading(false);
    }
  };

  if (!isHearing) {
    return (
      <Typography variant="caption" color="text.secondary">
        Not a hearing — no appearance needed
      </Typography>
    );
  }

  const confirmed = Boolean(setAt);
  const willEmail = pending !== null && pending !== 'NOT_REQUIRED' && !isPast;

  const apply = async (next: AppearanceRequirement) => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const updated = await CourtEventApi.updateCourtEvent({
        CourtEvent_guid: courtEventGuid,
        CourtEvent_AppearanceRequirement: next,
        ...(chosenGuid !== undefined ? { CourtEvent_Court_guid: chosenGuid } : {}),
      });
      setCurrentValue(next);
      onChanged(next);

      const result = updated.appearanceNotification;
      if (result?.sent) {
        setNotice('Saved. The client has been emailed, and a copy is in the case notes.');
      } else if (next === 'NOT_REQUIRED') {
        // Fix error-like banner text when changing to NOT_REQUIRED (Requirement 7)
        setNotice('Saved. Client does not need to appear; no message sent to client.');
      } else if (result?.skippedReason) {
        setNotice(`Saved, but the client was not emailed — ${result.skippedReason}.`);
      } else {
        setNotice('Saved.');
      }
      closePendingDialog();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <FormControl size="small" fullWidth disabled={disabled || saving || notRequiredSending}>
        <Select
          value={currentValue}
          onChange={(e) => {
            const next = e.target.value as AppearanceRequirement;
            if (next === currentValue) return;

            if (next === 'NOT_REQUIRED') {
              // Saving and sending are separate: change saves immediately
              void (async () => {
                await apply(next);
                // If future date, switch ON, and not already told -> prompt the dialog
                if (!isPast && switchEnabled && !toldAt) {
                  void openNotRequiredDialog();
                }
              })();
            } else {
              // Must-attend settings confirm via pending dialog
              setPending(next);
            }
          }}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {ICONS[selected as AppearanceRequirement]}
              {APPEARANCE_LABELS[selected as AppearanceRequirement]}
            </Box>
          )}
        >
          {(Object.keys(APPEARANCE_LABELS) as AppearanceRequirement[]).map((key) => (
            <MenuItem key={key} value={key}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {ICONS[key]}
                {APPEARANCE_LABELS[key]}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ mt: 0.5 }}>
        {confirmed ? (
          <Chip
            size="small"
            color="success"
            variant="outlined"
            label={`Confirmed by ${setBy || 'staff'}${
              setAt ? ` on ${new Date(setAt).toLocaleDateString('en-US')}` : ''
            }`}
          />
        ) : (
          <Chip size="small" color="warning" variant="outlined" label="Unconfirmed — nobody has checked this" />
        )}
      </Box>

      {/* Tell the client button / stamp on future NOT_REQUIRED dates */}
      {currentValue === 'NOT_REQUIRED' && isHearing && !isPast && switchEnabled && (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {toldAt ? (
            <>
              <Chip
                size="small"
                color="info"
                variant="outlined"
                label={`Client told on ${new Date(toldAt).toLocaleDateString('en-US')} by ${toldBy || 'staff'}`}
              />
              <Button
                size="small"
                variant="outlined"
                disabled={saving || notRequiredSending}
                onClick={() => void openNotRequiredDialog()}
              >
                Tell again
              </Button>
            </>
          ) : (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              disabled={saving || notRequiredSending}
              onClick={() => void openNotRequiredDialog()}
            >
              Tell the client
            </Button>
          )}
        </Box>
      )}

      {notice && (
        <Alert severity="info" sx={{ mt: 1 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Pending Dialog for IN_PERSON, BY_VIDEO, BY_PHONE */}
      <Dialog open={pending !== null} onClose={() => !saving && closePendingDialog()} maxWidth="md" fullWidth>
        <DialogTitle>
          {willEmail ? 'This will email the client' : 'Change the appearance answer'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You are recording: <strong>{pending ? APPEARANCE_LABELS[pending] : ''}</strong>
          </Typography>

          {willEmail && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              The client will be emailed straight away telling them the court requires their
              attendance
              {pending === 'BY_VIDEO' ? ', by video' : pending === 'BY_PHONE' ? ', by phone' : ' in person'}, and texted if we have their
              mobile number. This cannot be undone — if it is wrong, you will need to call them.
            </Alert>
          )}

          {willEmail && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Which court is this date in?
              </Typography>
              <CourtSelect
                label="Court for this date"
                value={
                  courtChoice !== undefined
                    ? courtChoice
                    : preview?.courtGuid
                      ? ({
                          Court_guid: preview.courtGuid,
                          Court_Name: preview.courtName ?? '',
                          Court_Address: preview.courtAddress ?? '',
                        } as unknown as CourtChoice)
                      : null
                }
                onChange={(court) => setCourtChoice(court ?? null)}
                disabled={saving}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Saved on this court date only. The case keeps its own court.
              </Typography>

              {previewLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2">Building the email…</Typography>
                </Box>
              )}
              {previewError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {previewError}
                </Alert>
              )}
              {!previewLoading && previewSkipped && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Nothing will be emailed: {previewSkipped}.
                </Alert>
              )}
              {!previewLoading &&
                preview?.warnings.map((warning) => (
                  <Alert key={warning} severity="warning" sx={{ mt: 2 }}>
                    {warning}
                  </Alert>
                ))}

              {!previewLoading && preview && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">What the client will get</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <strong>To:</strong> {preview.recipient ?? 'no email address on file'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Subject:</strong> {preview.subject}
                  </Typography>
                  <Box
                    component="iframe"
                    title="Email preview"
                    sandbox=""
                    srcDoc={preview.html}
                    sx={{ width: '100%', height: 360, border: 1, borderColor: 'divider', borderRadius: 1, mt: 1, bgcolor: '#fff' }}
                  />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Text message:</strong> {preview.sms ?? 'none (no mobile number on file)'}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {isPast && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This court date has already passed, so nothing is sent to the client. The answer is
              recorded for the file.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePendingDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={willEmail ? 'warning' : 'primary'}
            disabled={saving || (willEmail && previewLoading)}
            onClick={() => pending && apply(pending)}
          >
            {saving ? 'Saving…' : willEmail ? 'Save and email the client' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tell Client Dialog for NOT_REQUIRED (Send and Don't send only) */}
      <Dialog
        open={notRequiredDialogOpen}
        onClose={() => {
          if (!notRequiredSending) {
            setNotRequiredDialogOpen(false);
            setNotice('Saved. Client does not need to appear; no message sent to client.');
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Tell the client they do not need to appear</DialogTitle>
        <DialogContent>
          {notRequiredLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 4 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading preview…</Typography>
            </Box>
          ) : notRequiredError ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {notRequiredError}
            </Alert>
          ) : notRequiredPreview ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Pre-filled with Lloyd's standard wording. You can edit the message below before
                sending. Nothing is sent unless you press Send.
              </Typography>

              {!notRequiredPreview.hasContactMethod && (
                <Alert severity="error">
                  This client has no email address or phone number on file. Nothing can be sent by
                  email or text.
                </Alert>
              )}

              <Box
                sx={{
                  p: 1.5,
                  bgcolor: 'background.default',
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Channels and Recipients:
                </Typography>
                <Typography variant="body2">
                  <strong>Email:</strong>{' '}
                  {notRequiredPreview.recipient || <em>No email address on file</em>}
                </Typography>
                <Typography variant="body2">
                  <strong>Text (SMS):</strong>{' '}
                  {notRequiredPreview.phone ? (
                    notRequiredPreview.mayText ? (
                      `${notRequiredPreview.phone} (consented to texts)`
                    ) : (
                      `${notRequiredPreview.phone} (client has not agreed to texts; will not be texted)`
                    )
                  ) : (
                    <em>No phone number on file</em>
                  )}
                </Typography>
                <Typography variant="body2">
                  <strong>Portal:</strong> Message will be recorded in the client portal thread.
                </Typography>
              </Box>

              <TextField
                label="Subject"
                size="small"
                fullWidth
                value={editedSubject}
                onChange={(e) => setEditedSubject(e.target.value)}
                disabled={notRequiredSending}
              />

              <TextField
                label="Message to Client"
                multiline
                rows={6}
                fullWidth
                value={editedBody}
                onChange={(e) => setEditedBody(e.target.value)}
                disabled={notRequiredSending}
                helperText={`${editedBody.length}/2000 characters`}
                error={editedBody.length > 2000 || editedBody.trim().length === 0}
              />

              {notRequiredPreview.phone && notRequiredPreview.mayText && (
                <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: 0.5, fontWeight: 'bold' }}
                  >
                    SMS preview (short pointer to email):
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                  >
                    {notRequiredPreview.sms}
                  </Typography>
                </Box>
              )}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setNotRequiredDialogOpen(false);
              setNotice('Saved. Client does not need to appear; no message sent to client.');
            }}
            disabled={notRequiredSending}
          >
            Don't send
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={
              notRequiredSending ||
              notRequiredLoading ||
              !notRequiredPreview?.hasContactMethod ||
              editedBody.trim().length === 0 ||
              editedBody.length > 2000
            }
            onClick={async () => {
              setNotRequiredSending(true);
              setNotRequiredError(null);
              try {
                await CourtEventApi.sendAppearanceNotRequiredNotice({
                  CourtEvent_guid: courtEventGuid,
                  body: editedBody,
                  subject: editedSubject,
                });
                setToldAt(new Date().toISOString());
                setToldBy('you');
                setNotRequiredDialogOpen(false);
                setNotice(
                  'Saved. The client has been told they do not need to appear, and a copy is in the case notes.',
                );
                onChanged('NOT_REQUIRED');
              } catch (err) {
                setNotRequiredError(err instanceof Error ? err.message : 'Failed to send message');
              } finally {
                setNotRequiredSending(false);
              }
            }}
          >
            {notRequiredSending ? 'Sending…' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppearanceControl;
